/**
 * <b2b-registration-form> — custom element for the B2B self-registration.
 *
 * Three-step wizard (contact details → shipping address → billing address)
 * that talks to the Rosental E-Com Operations app (Vercel):
 *   POST {api}/api/b2b/vat-check  → live VIES feedback while typing
 *   POST {api}/api/b2b/register   → verify + create company/contact
 *
 * The element expects:
 *   data-api-base             Base URL of the API (no trailing slash)
 *   data-turnstile            Turnstile site key (optional → skips captcha)
 *   data-vat-countries        Allowed VAT ID countries, e.g. "DE,AT" (empty = all)
 *   data-other-business-type  Business type without B2B approval ("Sonstiges")
 *   data-strings              JSON blob with localized messages
 */
(() => {
  if (customElements.get('b2b-registration-form')) return;

  /** Client-side VAT format patterns (number part, without country prefix). */
  const VAT_FORMATS = {
    AT: /^U\d{8}$/, BE: /^[01]\d{9}$/, BG: /^\d{9,10}$/, CY: /^\d{8}[A-Z]$/,
    CZ: /^\d{8,10}$/, DE: /^\d{9}$/, DK: /^\d{8}$/, EE: /^\d{9}$/,
    EL: /^\d{9}$/, ES: /^[A-Z0-9]\d{7}[A-Z0-9]$/, FI: /^\d{8}$/,
    FR: /^[A-Z0-9]{2}\d{9}$/, HR: /^\d{11}$/, HU: /^\d{8}$/,
    IE: /^(\d{7}[A-W][A-I]?|\d[A-Z+*]\d{5}[A-W])$/, IT: /^\d{11}$/,
    LT: /^(\d{9}|\d{12})$/, LU: /^\d{8}$/, LV: /^\d{11}$/, MT: /^\d{8}$/,
    NL: /^\d{9}B\d{2}$/, PL: /^\d{10}$/, PT: /^\d{9}$/, RO: /^\d{2,10}$/,
    SE: /^\d{12}$/, SI: /^\d{8}$/, SK: /^\d{10}$/,
    XI: /^(\d{9}|\d{12}|GD\d{3}|HA\d{3})$/,
    // Switzerland (not part of VIES): CHE + 9 digits, e.g. CHE-123.456.789 MWST
    CHE: /^\d{9}$/,
  };

  /** Display prefix per country (what the customer actually types). */
  const DISPLAY_PREFIX = { AT: 'ATU', CHE: 'CHE' };

  /** Placeholder shown in the VAT input per selected country. */
  const VAT_PLACEHOLDERS = {
    DE: 'DE123456789',
    NL: 'NL123456789B01',
    AT: 'ATU12345678',
    BE: 'BE0123456789',
    CHE: 'CHE-123.456.789',
  };

  const normalizeVat = (raw) => {
    let value = (raw || '').toUpperCase().replace(/[\s.\-–—/,]/g, '');
    if (value.startsWith('GR')) value = 'EL' + value.slice(2);
    // Swiss IDs are often written with a "MWST"/"TVA"/"IVA" suffix.
    if (value.startsWith('CHE')) value = value.replace(/(MWST|TVA|IVA)$/, '');
    return value;
  };

  /** Country key of a normalized VAT ID ("CHE" is a 3-letter prefix). */
  const vatCountry = (value) =>
    value.startsWith('CHE') ? 'CHE' : value.slice(0, 2);

  const vatFormatOk = (raw) => {
    const value = normalizeVat(raw);
    if (value.length < 4) return false;
    const country = vatCountry(value);
    const pattern = VAT_FORMATS[country];
    return !!pattern && pattern.test(value.slice(country.length));
  };

  class B2BRegistrationForm extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form');
      if (!this.form) return;

      this.apiBase = (this.dataset.apiBase || '').replace(/\/$/, '');
      this.strings = JSON.parse(this.dataset.strings || '{}');
      this.statusEl = this.querySelector('[data-b2b-status]');
      this.vatInput = this.form.querySelector('[name="vat_id"]');
      this.vatStatusEl = this.querySelector('[data-b2b-vat-status]');
      this.submitButton = this.form.querySelector('button[type="submit"]');
      this.vatState = 'unknown';

      // Allowed VAT ID countries, e.g. "DE,AT" (CHE possible). Empty = all.
      this.vatCountries = (this.dataset.vatCountries || '')
        .toUpperCase()
        .split(',')
        .map((c) => c.trim())
        .filter(Boolean);

      this.form.addEventListener('submit', (e) => this.onSubmit(e));

      // VAT country select → placeholder + format hint follow the selection.
      this.vatCountrySelect = this.form.querySelector('[name="vat_country"]');
      this.vatHintEl = this.querySelector('[data-b2b-vat-hint]');
      if (this.vatCountrySelect) {
        this.vatCountrySelect.addEventListener('change', () => {
          this.vatState = 'unknown';
          this.renderVatStatus('');
          this.updateVatCountryUI();
          if (this.vatInput?.value?.trim()) this.checkVat();
        });
        this.updateVatCountryUI();
      }

      if (this.vatInput) {
        this.vatInput.addEventListener('blur', () => this.checkVat());
        this.vatInput.addEventListener('input', () => {
          this.vatState = 'unknown';
          this.renderVatStatus('');
          this.syncCountryFromInput();
        });
      }

      // Business type: intentionally NO inline hint for "Sonstiges"/"Other" —
      // the (neutral) rejection only shows after submitting, so customers
      // can't game the outcome by just picking a different category.
      this.businessTypeSelect = this.form.querySelector('[name="business_type"]');

      // --- Step wizard -----------------------------------------------------
      this.steps = [...this.querySelectorAll('[data-b2b-step]')];
      this.progressEl = this.querySelector('[data-b2b-progress]');
      this.currentStep = 1;
      this.querySelectorAll('[data-b2b-next]').forEach((btn) =>
        btn.addEventListener('click', () => this.goToStep(this.currentStep + 1))
      );
      this.querySelectorAll('[data-b2b-back]').forEach((btn) =>
        btn.addEventListener('click', () => this.showStep(this.currentStep - 1))
      );

      // --- Billing address toggle -------------------------------------------
      this.billingToggle = this.form.querySelector('[name="billing_different"]');
      this.billingContainer = this.querySelector('[data-b2b-billing]');
      if (this.billingToggle && this.billingContainer) {
        this.billingToggle.addEventListener('change', () =>
          this.setBillingEnabled(this.billingToggle.checked)
        );
        this.setBillingEnabled(this.billingToggle.checked);
      }

      // Double opt-in return leg: the confirm endpoint redirects here with
      // ?b2b_confirm=ok|invalid|vat_taken|error (+ optional b2b_confirm_detail
      // with the technical reason from Vercel) after the customer clicked the link.
      const confirmParams = new URLSearchParams(window.location.search);
      const confirmState = confirmParams.get('b2b_confirm');
      if (confirmState === 'ok') {
        this.showSuccess();
      } else if (confirmState === 'vat_taken') {
        this.showError(this.strings.vat_taken || this.strings.internal_error);
      } else if (confirmState === 'invalid' || confirmState === 'error') {
        this.showError(
          this.strings['confirm_' + confirmState] || this.strings.internal_error,
          confirmParams.get('b2b_confirm_detail') || undefined
        );
      }

      this.mountTurnstile();
    }

    /* -------------------------------------------------------------- Wizard */

    stepEl(step) {
      return this.steps.find((s) => Number(s.dataset.b2bStep) === step);
    }

    showStep(step) {
      const clamped = Math.min(Math.max(step, 1), this.steps.length);
      this.currentStep = clamped;
      this.steps.forEach((s) => {
        s.hidden = Number(s.dataset.b2bStep) !== clamped;
      });
      if (this.progressEl) {
        [...this.progressEl.children].forEach((bar, idx) => {
          bar.classList.toggle('is-active', idx < clamped);
        });
      }
      this.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }

    /** Advance only when every visible field of the current step is valid. */
    goToStep(step) {
      if (step > this.currentStep && !this.validateStep(this.currentStep)) return;
      this.showStep(step);
    }

    validateStep(step) {
      const el = this.stepEl(step);
      if (!el) return true;
      for (const field of el.querySelectorAll('input, select, textarea')) {
        if (field.disabled) continue;
        if (!field.checkValidity()) {
          field.reportValidity();
          return false;
        }
      }
      // Step 1 additionally gates on the VAT ID format/country.
      if (step === 1) {
        if (!this.vatInputOk()) {
          this.vatInput?.focus();
          return false;
        }
      }
      return true;
    }

    /* -------------------------------------------------------------- Billing */

    setBillingEnabled(enabled) {
      if (!this.billingContainer) return;
      this.billingContainer.hidden = !enabled;
      this.billingContainer
        .querySelectorAll('input, select')
        .forEach((field) => {
          field.disabled = !enabled;
        });
    }

    billingEnabled() {
      return !!(this.billingToggle?.checked && this.billingContainer);
    }

    /* ---------------------------------------------------------- Turnstile */

    mountTurnstile() {
      const siteKey = this.dataset.turnstile;
      const slot = this.querySelector('[data-b2b-turnstile]');
      if (!siteKey || !slot) return;

      const render = () => {
        window.turnstile.render(slot, {
          sitekey: siteKey,
          callback: (token) => {
            this.turnstileToken = token;
          },
          'expired-callback': () => {
            this.turnstileToken = null;
          },
        });
      };

      if (window.turnstile) {
        render();
      } else {
        const script = document.createElement('script');
        script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
        script.async = true;
        script.onload = render;
        document.head.appendChild(script);
      }
    }

    /* ------------------------------------------------------ VAT live check */

    /** Currently selected VAT country ("DE", "NL", "AT", "CHE"). */
    selectedVatCountry() {
      return String(this.vatCountrySelect?.value || 'DE').toUpperCase();
    }

    /** Placeholder + format hint follow the selected country. */
    updateVatCountryUI() {
      const country = this.selectedVatCountry();
      if (this.vatInput && VAT_PLACEHOLDERS[country]) {
        this.vatInput.placeholder = VAT_PLACEHOLDERS[country];
      }
      if (this.vatHintEl) {
        this.vatHintEl.textContent = (this.strings.vat_hints || {})[country] || '';
      }
    }

    /** Pasting a full VAT ID (e.g. "NL…" while DE is selected) switches the country. */
    syncCountryFromInput() {
      if (!this.vatCountrySelect || !this.vatInput) return;
      const raw = normalizeVat(this.vatInput.value);
      if (raw.length < 3) return;
      const options = [...this.vatCountrySelect.options].map((o) => o.value.toUpperCase());
      for (const code of options) {
        const prefix = DISPLAY_PREFIX[code] || code;
        if ((raw.startsWith(prefix) || (code === 'AT' && raw.startsWith('AT'))) && code !== this.selectedVatCountry()) {
          this.vatCountrySelect.value = code;
          this.updateVatCountryUI();
          break;
        }
      }
    }

    /**
     * Full normalized VAT ID: selected country prefix + typed number.
     * Tolerates inputs with or without the prefix ("123456789",
     * "DE123456789", "U12345678" for AT, "CHE-123.456.789 MWST").
     */
    fullVat() {
      const raw = normalizeVat(this.vatInput?.value || '');
      if (!raw) return '';
      const country = this.selectedVatCountry();
      const prefix = DISPLAY_PREFIX[country] || country;
      if (raw.startsWith(prefix)) return raw;
      if (country === 'AT') {
        if (raw.startsWith('U')) return 'AT' + raw; // "U12345678"
        if (raw.startsWith('AT')) return 'ATU' + raw.slice(2); // "AT12345678"
      }
      return prefix + raw;
    }

    renderVatStatus(html, modifier) {
      if (!this.vatStatusEl) return;
      this.vatStatusEl.innerHTML = html;
      this.vatStatusEl.dataset.state = modifier || '';
    }

    /** Localized error for a VAT ID from a country we don't accept. */
    vatCountryError() {
      const prefixes = this.vatCountries
        .map((c) => DISPLAY_PREFIX[c] || c)
        .join(', ');
      return (this.strings.vat_country || '').replace('{{ prefixes }}', prefixes);
    }

    /** Format + country check combined; renders the matching message. */
    vatInputOk() {
      const full = this.fullVat();
      if (!vatFormatOk(full)) {
        this.renderVatStatus(this.strings.vat_format, 'error');
        return false;
      }
      const country = vatCountry(full);
      if (this.vatCountries.length && !this.vatCountries.includes(country)) {
        this.renderVatStatus(this.vatCountryError(), 'error');
        return false;
      }
      return true;
    }

    async checkVat() {
      const full = this.fullVat();
      if (!full) return;

      if (!this.vatInputOk()) {
        this.vatState = 'invalid';
        return;
      }

      // CHE included: the API checks Swiss IDs against the UID register.
      this.renderVatStatus(this.strings.vat_checking, 'pending');
      try {
        const res = await fetch(`${this.apiBase}/api/b2b/vat-check`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ vatId: full }),
        });
        const json = await res.json();

        if (json.status === 'valid') {
          this.vatState = 'valid';
          const name = json.name ? ` — ${json.name}` : '';
          this.renderVatStatus(`${this.strings.vat_valid}${name}`, 'success');
        } else if (json.status === 'invalid') {
          this.vatState = 'invalid';
          this.renderVatStatus(this.strings.vat_invalid, 'error');
        } else {
          // VIES down → don't block typing; server re-checks on submit.
          this.vatState = 'unknown';
          this.renderVatStatus(this.strings.vat_unavailable, 'pending');
        }
      } catch {
        this.vatState = 'unknown';
        this.renderVatStatus('', '');
      }
    }

    /* ------------------------------------------------------------- Submit */

    setLoading(loading) {
      if (!this.submitButton) return;
      this.submitButton.toggleAttribute('disabled', loading);
      this.submitButton.classList.toggle('button--loading', loading);
    }

    /**
     * Friendly error banner: intro ("could not be completed because of:"),
     * the specific reason, an optional technical detail from the backend
     * (validation/API messages), and a retry hint incl. the D2C shop link.
     */
    showError(message, detail) {
      if (!this.statusEl) return;
      const parts = [];
      if (this.strings.error_intro) parts.push(`<p>${this.strings.error_intro}</p>`);
      parts.push(`<p><strong>${message}</strong></p>`);
      if (detail) {
        // API detail is data, not markup — escape before rendering.
        const esc = String(detail).replace(/[&<>"']/g, (c) => (
          { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]
        ));
        const label = this.strings.error_detail_label || 'Details';
        parts.push(`<p class="text-sm text-subdued">${label}: ${esc}</p>`);
      }
      if (this.strings.error_retry_html) parts.push(`<p>${this.strings.error_retry_html}</p>`);
      // Soft notice styling (amber) instead of a harsh red error banner.
      this.statusEl.innerHTML = `<div class="banner banner--warning text-start"><div class="banner__content v-stack gap-2">${parts.join('')}</div></div>`;
      this.statusEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }

    /** Hide the form and show a result panel ([data-b2b-success] / [data-b2b-not-eligible]). */
    showPanel(selector) {
      const panel = this.querySelector(selector);
      if (!panel) return false;
      this.form.hidden = true;
      // Card heading/subtitle live OUTSIDE this element ("Jetzt registrieren" /
      // "…sofort startklar") and would contradict the panel (e.g. the DOI
      // pending box) — show only the panel.
      const card = this.closest('.b2b-reg__card');
      if (card) {
        card.querySelectorAll('.b2b-reg__card-heading, .b2b-reg__card-sub').forEach((el) => {
          el.hidden = true;
        });
      }
      if (this.statusEl) this.statusEl.innerHTML = '';
      panel.hidden = false;
      panel.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return true;
    }

    showSuccess() {
      // Short-lived bridge cookie for the verification gate: covers the
      // metafield propagation delay right after registration. Must NOT
      // bypass the gate long-term — the company metafield b2b.vat_verified
      // is the source of truth.
      try {
        document.cookie = 'b2b_vat_bridge=1; path=/; max-age=900; SameSite=Lax';
      } catch (e) { /* noop */ }
      this.showPanel('[data-b2b-success]');
    }

    /** Double opt-in: confirmation mail sent, waiting for the click. */
    showPending(email) {
      const target = this.querySelector('[data-b2b-pending-email]');
      if (target && email) target.textContent = email;
      if (!this.showPanel('[data-b2b-pending]')) {
        this.showPanel('[data-b2b-success]');
      }
    }

    showNotEligible() {
      if (!this.showPanel('[data-b2b-not-eligible]')) {
        // No dedicated panel configured → fall back to the localized text.
        this.showPanel('[data-b2b-success]');
      }
    }

    async onSubmit(event) {
      event.preventDefault();
      if (!this.validateStep(3)) return;

      const data = new FormData(this.form);

      // The VAT ID was validated in step 1 — re-check defensively (the
      // customer may have navigated back and edited it).
      if (!this.vatInputOk()) {
        this.showStep(1);
        this.vatInput?.focus();
        return;
      }

      const readAddress = (prefix) => ({
        recipient: String(data.get(`${prefix}recipient`) || '').trim(),
        address1: String(data.get(`${prefix}address1`) || '').trim(),
        address2: String(data.get(`${prefix}address2`) || '').trim(),
        zip: String(data.get(`${prefix}zip`) || '').trim(),
        city: String(data.get(`${prefix}city`) || '').trim(),
        countryCode: String(data.get(`${prefix === '' ? 'shipping_' : prefix}country`) || 'DE'),
      });

      // Phone: prepend the selected dial code unless the customer already
      // typed an international number ("+49…" / "0049…").
      const phoneRaw = String(data.get('phone') || '').trim();
      const dialCode = String(data.get('phone_country') || '').trim();
      let phone = phoneRaw;
      if (phoneRaw && dialCode && !/^(\+|00)/.test(phoneRaw)) {
        phone = dialCode + phoneRaw.replace(/^0+/, '').replace(/[\s\-/()]/g, '');
      }

      const payload = {
        company: {
          name: String(data.get('company_name') || '').trim(),
          vatId: this.fullVat(),
          businessType: String(data.get('business_type') || '').trim(),
        },
        contact: {
          firstName: String(data.get('first_name') || '').trim(),
          lastName: String(data.get('last_name') || '').trim(),
          email: String(data.get('email') || '').trim(),
          phone,
        },
        shippingAddress: readAddress(''),
        billingAddress: this.billingEnabled() ? readAddress('billing_') : null,
        locale: document.documentElement.lang || 'de',
        turnstileToken: this.turnstileToken || undefined,
      };

      this.setLoading(true);
      try {
        const res = await fetch(`${this.apiBase}/api/b2b/register`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
        const json = await res.json().catch(() => ({}));

        if (json.ok) {
          // pending:true = double opt-in — nothing is created yet, the
          // customer must click the confirmation link we just emailed.
          if (json.pending) {
            this.showPending(String(data.get('email') || '').trim());
          } else if (json.approved === false) {
            // approved:false = submission accepted, but no B2B access
            // (business type "Sonstiges") — customer is notified by email.
            this.showNotEligible();
          } else {
            this.showSuccess();
          }
          return;
        }
        const message = this.strings[json.error] || this.strings.internal_error;
        this.showError(message, json.detail);
        // VAT errors live in step 1 — bring the customer back there.
        if (String(json.error || '').startsWith('vat')) {
          this.showStep(1);
        }
      } catch {
        this.showError(this.strings.internal_error);
      } finally {
        this.setLoading(false);
      }
    }
  }

  customElements.define('b2b-registration-form', B2BRegistrationForm);
})();
